"use client";

import {
  ArrowRight,
  BadgeCheck,
  Download,
  Flame,
  Languages,
  LockKeyhole,
  Play,
  Search,
  ShoppingBag,
  Smartphone,
  Trophy,
  Users,
  Zap,
} from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/language-context";
import {
  ANDROID_PLAY_STORE_URL,
  BIGQUIZ_PROFILE_URL,
  IOS_APP_STORE_URL,
} from "@/lib/store-links";

const gameCards = [
  {
    titleKey: "landing.games.general",
    color: "#7DD3FC",
    online: "5,431",
    mascot: "#8B5CF6",
    unlocked: true,
  },
  {
    titleKey: "landing.games.science",
    color: "#FDBA74",
    online: "6,284",
    mascot: "#F59E0B",
    unlocked: true,
  },
  {
    titleKey: "landing.games.cinema",
    color: "#F9A8D4",
    online: "4,892",
    mascot: "#14B8A6",
    unlocked: false,
  },
];

const quickStats = [
  { labelKey: "landing.stats.games", value: "10k+" },
  { labelKey: "landing.stats.energy", value: "45" },
  { labelKey: "landing.stats.level", value: "8" },
];

const appFeatures = [
  {
    icon: Flame,
    titleKey: "landing.features.quickTitle",
    textKey: "landing.features.quickText",
    color: "#FFD84D",
  },
  {
    icon: Trophy,
    titleKey: "landing.features.missionsTitle",
    textKey: "landing.features.missionsText",
    color: "#A7F3D0",
  },
  {
    icon: Users,
    titleKey: "landing.features.duelTitle",
    textKey: "landing.features.duelText",
    color: "#C4B5FD",
  },
];

const platformBadges = [
  {
    nameKey: "landing.platforms.android.name",
    statusKey: "landing.platforms.android.status",
    detailKey: "landing.platforms.android.detail",
    color: "#A7F3D0",
    icon: BadgeCheck,
    href: ANDROID_PLAY_STORE_URL,
  },
  {
    nameKey: "landing.platforms.ios.name",
    statusKey: "landing.platforms.ios.status",
    detailKey: "landing.platforms.ios.detail",
    color: "#E8E5FF",
    icon: Smartphone,
    href: IOS_APP_STORE_URL,
  },
];

const questionOptionKeys = [
  "landing.quiz.options.mars",
  "landing.quiz.options.jupiter",
  "landing.quiz.options.venus",
  "landing.quiz.options.saturn",
];

const missionItems = [
  { titleKey: "landing.missions.win", progress: "2/3", width: "66%" },
  { titleKey: "landing.missions.answer", progress: "14/20", width: "70%" },
  { titleKey: "landing.missions.duel", progress: "1/1", width: "100%" },
];

export default function BigQuizLanding() {
  const { t } = useLanguage();

  return (
    <main className="bg-[#F7F4EA] text-[#111111]">
      <section className="px-4 pb-14 pt-8 md:pb-20 md:pt-12">
        <div className="container mx-auto grid gap-10 lg:grid-cols-[0.92fr_1.08fr] lg:items-center">
          <div>
            <a
              href={BIGQUIZ_PROFILE_URL}
              aria-label="Open profile"
              className="mb-8 flex w-fit items-center gap-2 rounded-full bg-white px-3 py-2 shadow-sm transition-transform hover:-translate-y-0.5"
            >
              <Image
                src="/logo.png"
                alt="BigQuiz"
                width={32}
                height={32}
                className="rounded-full"
              />
              <span className="text-sm font-black">{t("landing.player")}</span>
              <span className="rounded-full bg-[#F7B731] px-2 py-1 text-xs font-black">
                2,000
              </span>
            </a>

            <p className="mb-3 text-sm font-black uppercase tracking-[0.18em] text-[#0F9F8F]">
              {t("landing.hero.badge")}
            </p>
            <h1 className="max-w-3xl text-5xl font-black leading-[0.95] tracking-tight md:text-7xl">
              {t("landing.hero.titleLine1")}
              <br />
              {t("landing.hero.titleLine2")}
            </h1>
            <p className="mt-6 max-w-xl text-lg font-bold leading-8 text-[#706D65]">
              {t("landing.hero.subtitle")}
            </p>

            <div className="mt-6 grid max-w-xl gap-3 sm:grid-cols-2">
              {platformBadges.map((platform) => {
                const Icon = platform.icon;
                const content = (
                  <>
                    <span
                      className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl"
                      style={{ backgroundColor: platform.color }}
                    >
                      <Icon className="h-5 w-5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block text-sm font-black uppercase text-[#111111]">
                        {t(platform.nameKey)}
                      </span>
                      <span className="block text-sm font-black text-[#0F9F8F]">
                        {t(platform.statusKey)}
                      </span>
                      <span className="block text-xs font-bold text-[#706D65]">
                        {t(platform.detailKey)}
                      </span>
                    </span>
                  </>
                );

                const isExternalLink = platform.href?.startsWith("http");

                return platform.href ? (
                  <Link
                    key={platform.nameKey}
                    href={platform.href}
                    target={isExternalLink ? "_blank" : undefined}
                    rel={isExternalLink ? "noopener noreferrer" : undefined}
                    className="flex min-h-[84px] items-center gap-3 rounded-[22px] bg-white p-3 shadow-sm transition-transform hover:-translate-y-0.5"
                  >
                    {content}
                  </Link>
                ) : (
                  <div
                    key={platform.nameKey}
                    className="flex min-h-[84px] items-center gap-3 rounded-[22px] bg-white p-3 shadow-sm"
                  >
                    {content}
                  </div>
                );
              })}
            </div>

            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Button
                asChild
                size="lg"
                className="h-auto min-h-14 whitespace-normal rounded-full bg-[#111111] px-7 py-3 text-center font-black leading-tight text-white hover:bg-[#2A2A2A] sm:h-14 sm:whitespace-nowrap"
              >
                <Link href="/download">
                  <Download className="mr-2 h-5 w-5" />
                  {t("landing.hero.downloadCta")}
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                className="h-auto min-h-14 whitespace-normal rounded-full bg-[#FFD84D] px-7 py-3 text-center font-black leading-tight text-[#111111] hover:bg-[#F7B731] sm:h-14 sm:whitespace-nowrap"
              >
                <Link href="#games">
                  <Play className="mr-2 h-5 w-5" />
                  {t("landing.hero.gamesCta")}
                </Link>
              </Button>
            </div>
          </div>

          <div className="rounded-[34px] bg-white p-4 shadow-sm md:p-5">
            <div className="mb-6 flex items-center justify-between">
              <a
                href={BIGQUIZ_PROFILE_URL}
                aria-label="Open profile"
                className="flex items-center gap-3 rounded-full bg-[#F7F4EA] p-2 pr-4 transition-transform hover:-translate-y-0.5"
              >
                <Image
                  src="/logo.png"
                  alt="BigQuiz"
                  width={34}
                  height={34}
                  className="rounded-full"
                />
                <span className="text-sm font-black">BigQuiz</span>
              </a>
              <div className="flex gap-2">
                <div className="grid h-10 w-10 place-items-center rounded-full bg-[#F7F4EA]">
                  <ShoppingBag className="h-5 w-5" />
                </div>
                <div className="grid h-10 w-10 place-items-center rounded-full bg-[#F7F4EA]">
                  <Search className="h-5 w-5" />
                </div>
              </div>
            </div>

            <div className="mb-5 flex items-end justify-between gap-4">
              <div>
                <h2 className="text-4xl font-black leading-none">
                  {t("landing.preview.titleLine1")}
                  <br />
                  {t("landing.preview.titleLine2")}
                </h2>
                <p className="mt-3 text-sm font-bold text-[#706D65]">
                  {t("landing.preview.energy")}
                </p>
              </div>
              <a
                href={BIGQUIZ_PROFILE_URL}
                aria-label="Open profile"
                className="flex items-center gap-2 rounded-full bg-[#F7F4EA] px-3 py-2 transition-transform hover:-translate-y-0.5"
              >
                <span className="text-sm font-black">
                  {t("landing.preview.locale")}
                </span>
                <Languages className="h-5 w-5 text-[#F7B731]" />
              </a>
            </div>

            <div id="games" className="grid gap-4 xl:grid-cols-3">
              {gameCards.map((card, index) => (
                <article
                  key={card.titleKey}
                  className="relative min-h-[260px] overflow-hidden rounded-[34px] p-6"
                  style={{ backgroundColor: card.color }}
                >
                  {!card.unlocked ? (
                    <div className="absolute right-5 top-5 z-10 flex items-center gap-2 rounded-full bg-white px-3 py-2 text-xs font-black">
                      <LockKeyhole className="h-4 w-4" />
                      2000
                    </div>
                  ) : null}
                  <div className="absolute right-5 top-16 h-5 w-5 rounded-full bg-white/80" />
                  <div className="absolute bottom-20 left-8 h-3 w-3 rounded-full bg-white/80" />

                  <div className="relative z-10 flex h-full flex-col justify-end">
                    <span className="mb-3 w-fit rounded-full bg-white px-5 py-2 text-lg font-black">
                      {t("landing.preview.quiz")}
                    </span>
                    <h3 className="max-w-[180px] text-3xl font-black leading-none">
                      {t(card.titleKey)}
                    </h3>
                    <p className="mt-3 text-xs font-black text-[#25302F]">
                      • {card.online} {t("landing.preview.online")}
                    </p>
                    <div className="mt-6 flex w-fit items-center gap-2 rounded-full bg-[#050505] px-6 py-3 text-sm font-black text-white">
                      {card.unlocked
                        ? t("landing.preview.play")
                        : t("landing.preview.unlock")}
                      <Play className="h-4 w-4 fill-white" />
                    </div>
                  </div>
                  <Mascot
                    className="absolute bottom-8 right-4"
                    color={card.mascot}
                    mood={index}
                  />
                </article>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-4 pb-16">
        <div className="container mx-auto grid gap-4 md:grid-cols-3">
          {quickStats.map((stat) => (
            <div
              key={stat.labelKey}
              className="rounded-[24px] bg-white p-6 text-center shadow-sm"
            >
              <p className="text-3xl font-black">{stat.value}</p>
              <p className="mt-1 text-sm font-black text-[#706D65]">
                {t(stat.labelKey)}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 pb-18 md:pb-24">
        <div className="container mx-auto grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
          <div className="rounded-[34px] bg-[#FFD84D] p-6 md:p-8">
            <div className="mb-8 flex h-14 w-14 items-center justify-center rounded-[20px] bg-white">
              <Zap className="h-7 w-7" />
            </div>
            <h2 className="text-4xl font-black leading-none md:text-5xl">
              {t("landing.quick.title")}
            </h2>
            <p className="mt-4 max-w-md text-base font-bold leading-7 text-[#5F4A00]">
              {t("landing.quick.description")}
            </p>
            <div className="mt-8 flex items-center justify-between rounded-[26px] bg-white p-4">
              <div>
                <p className="text-lg font-black">
                  {t("landing.quick.runTitle")}
                </p>
                <p className="text-sm font-bold text-[#706D65]">
                  {t("landing.quick.runDescription")}
                </p>
              </div>
              <ArrowRight className="h-6 w-6" />
            </div>
          </div>

          <div className="rounded-[34px] bg-white p-6 shadow-sm md:p-8">
            <div className="mb-5 flex items-center justify-between">
              <h2 className="text-3xl font-black leading-none">
                {t("landing.quiz.title")}
              </h2>
              <div className="rounded-full bg-[#FFD84D] px-4 py-2 text-sm font-black">
                20s
              </div>
            </div>

            <div className="mb-6 flex gap-2">
              {Array.from({ length: 10 }).map((_, index) => (
                <div
                  key={index}
                  className={`h-2 flex-1 rounded-full ${
                    index < 4 ? "bg-[#0F9F8F]" : "bg-[#D8D6CE]"
                  }`}
                />
              ))}
            </div>

            <h3 className="text-3xl font-black leading-tight">
              {t("landing.quiz.question")}
            </h3>
            <p className="mt-2 text-sm font-black text-[#716F68]">
              {t("landing.quiz.meta")}
            </p>

            <div className="mt-6 rounded-[30px] bg-[#FFD84D] p-4">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-black text-[#6F5B00]">3/10</span>
                <span className="rotate-6 rounded-full bg-[#7EDC87] px-3 py-2 text-xs font-black text-white">
                  {t("landing.quiz.correct")}
                </span>
              </div>
              <div className="space-y-3">
                {questionOptionKeys.map((optionKey, index) => (
                  <div
                    key={optionKey}
                    className={`flex min-h-14 items-center gap-3 rounded-full p-2 pr-4 ${
                      index === 0 ? "bg-[#CFFFD1]" : "bg-white"
                    }`}
                  >
                    <span className="grid h-9 w-9 place-items-center rounded-full bg-[#E7FFF8] text-xs font-black text-[#0F9F8F]">
                      {String.fromCharCode(65 + index)}
                    </span>
                    <span className="flex-1 text-sm font-black">
                      {t(optionKey)}
                    </span>
                    {index === 0 ? (
                      <BadgeCheck className="h-5 w-5 text-[#0F9F8F]" />
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="features" className="px-4 pb-18 md:pb-24">
        <div className="container mx-auto">
          <div className="mb-8 flex flex-col justify-between gap-4 md:flex-row md:items-end">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.18em] text-[#ED7AC7]">
                {t("landing.features.eyebrow")}
              </p>
              <h2 className="mt-3 text-4xl font-black leading-none md:text-5xl">
                {t("landing.features.title")}
              </h2>
            </div>
            <p className="max-w-xl text-base font-bold leading-7 text-[#706D65]">
              {t("landing.features.description")}
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            {appFeatures.map((feature) => {
              const Icon = feature.icon;

              return (
                <div
                  key={feature.titleKey}
                  className="rounded-[26px] p-6 shadow-sm"
                  style={{ backgroundColor: feature.color }}
                >
                  <div className="mb-6 flex h-14 w-14 items-center justify-center rounded-[20px] bg-white">
                    <Icon className="h-7 w-7" />
                  </div>
                  <h3 className="text-2xl font-black">
                    {t(feature.titleKey)}
                  </h3>
                  <p className="mt-3 text-sm font-bold leading-6 text-[#4E493F]">
                    {t(feature.textKey)}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section id="about" className="px-4 pb-20">
        <div className="container mx-auto grid gap-5 lg:grid-cols-[1fr_1fr]">
          <div className="rounded-[34px] bg-[#111111] p-6 text-white md:p-8">
            <a
              href={BIGQUIZ_PROFILE_URL}
              aria-label="Open profile"
              className="inline-flex h-12 w-12 items-center justify-center rounded-[18px] transition-colors hover:bg-white/10"
            >
              <Languages className="h-9 w-9 text-[#FFD84D]" />
            </a>
            <h2 className="mt-6 text-4xl font-black leading-none">
              {t("landing.about.titleLine1")}
              <br />
              {t("landing.about.titleLine2")}
            </h2>
            <p className="mt-4 max-w-lg text-base font-bold leading-7 text-white/70">
              {t("landing.about.description")}
            </p>
          </div>

          <div className="rounded-[34px] bg-white p-6 shadow-sm md:p-8">
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-3xl font-black">
                {t("landing.missions.title")}
              </h2>
              <span className="font-black text-[#0F9F8F]">2/3</span>
            </div>
            {missionItems.map((mission) => (
              <div key={mission.titleKey} className="mb-4 last:mb-0">
                <div className="mb-2 flex justify-between gap-4">
                  <span className="font-black">{t(mission.titleKey)}</span>
                  <span className="text-sm font-black text-[#706D65]">
                    {mission.progress} · {t("landing.missions.reward")}
                  </span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-[#E5E2D9]">
                  <div
                    className="h-full rounded-full bg-[#0F9F8F]"
                    style={{ width: mission.width }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="container mx-auto mt-5 rounded-[34px] bg-[#FFD84D] p-6 md:p-8">
          <div className="grid gap-5 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <p className="text-sm font-black uppercase tracking-[0.18em] text-[#6F5B00]">
                {t("landing.finalCta.eyebrow")}
              </p>
              <h2 className="mt-3 max-w-3xl text-4xl font-black leading-none md:text-5xl">
                {t("landing.finalCta.title")}
              </h2>
              <p className="mt-4 max-w-2xl text-base font-bold leading-7 text-[#6F5B00]">
                {t("landing.finalCta.description")}
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row md:justify-end">
              <Button
                asChild
                size="lg"
                className="h-auto min-h-14 whitespace-normal rounded-full bg-[#111111] px-8 py-3 text-center font-black leading-tight text-white hover:bg-[#2A2A2A] sm:h-14 sm:whitespace-nowrap"
              >
                <Link
                  href={ANDROID_PLAY_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("landing.finalCta.button")}
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button
                asChild
                size="lg"
                className="h-auto min-h-14 whitespace-normal rounded-full bg-white px-8 py-3 text-center font-black leading-tight text-[#111111] hover:bg-[#F7F4EA] sm:h-14 sm:whitespace-nowrap"
              >
                <Link
                  href={IOS_APP_STORE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t("landing.finalCta.iosButton")}
                  <Smartphone className="ml-2 h-5 w-5" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

function Mascot({
  color,
  mood,
  className,
}: {
  color: string;
  mood: number;
  className?: string;
}) {
  return (
    <div
      className={`h-[130px] w-[110px] rotate-[-5deg] rounded-b-[28px] rounded-t-full ${className || ""}`}
      style={{ backgroundColor: color }}
    >
      <div className="flex h-full flex-col items-center justify-center">
        <div className="flex gap-4">
          <span className="h-6 w-5 rounded-full border-[5px] border-[#111111] bg-white" />
          <span className="h-6 w-5 rounded-full border-[5px] border-[#111111] bg-white" />
        </div>
        <span
          className={`mt-4 block bg-[#111111] ${
            mood === 1
              ? "h-2 w-8 rounded-full"
              : "h-4 w-7 rounded-b-full rounded-t-sm"
          }`}
        />
      </div>
      <div className="absolute -bottom-3 left-6 h-5 w-7 rounded-full bg-[#4C1D95]" />
      <div className="absolute -bottom-3 right-6 h-5 w-7 rounded-full bg-[#4C1D95]" />
    </div>
  );
}
